import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLazyGetUserQuery } from "../service/auth";
import useAuth from "./useAuth";

export interface Permission {
  label: string;
  isAdd: boolean;
  isView: boolean;
  isDelete: boolean;
  isEdit: boolean;
  isSubMenu?: boolean;
  subMenuItems?: Permission[];
}

type SidebarLabels =
  | "Dashboard"
  | "Manage Users"
  | "Manage Payments"
  | "Notification Management"
  | "Manage Reviews"
  | "Manage Product Listing"
  | "Manage Boost Plans"
  | "Manage Product Categories"
  | "Manage Sub-Admin"
  | "Manage Customer Support"
  | "Manage CMS Pages";

type SidebarLabelsChild = never;

type PagePermission = "isView" | "isAdd" | "isEdit" | "isDelete";

interface UsePermissionProps {
  label: SidebarLabels;
  child?: SidebarLabelsChild;
  page_permission: PagePermission;
}

const usePermission = ({
  label,
  child,
  page_permission,
}: UsePermissionProps) => {
  const navigate = useNavigate();
  const userData = useAuth();
  const [isCheckingPermission, setIsCheckingPermission] = useState(false);

  // if(!userData ||  userData === undefined || userData === null || Object.keys(userData).length === 0 ){
  //       getUser()
  // }

  // Memoize permission calculation
  const permission: Permission | null = useMemo(() => {
    if (!userData) return null;
    const permissions = userData.accessRole?.permissions as
      | Permission[]
      | undefined;
    if (!permissions) return null;
    const matchedPermission = permissions.find((perm) => perm.label === label);
    return matchedPermission || null;
  }, [userData, label]);

  // Handle initial loading and redirects using promises
  useEffect(() => {
    const checkPermissionAndRedirect = async () => {
      // If already checking or no need to check, return early
      if (isCheckingPermission) {
        return;
      }

      setIsCheckingPermission(true);

      try {
        // // Create a promise to handle the loading state
        // await new Promise<void>((resolve) => {
        //   if (!isLoading) {
        //     resolve();
        //   } else {
        //     // If loading, wait for it to complete
        //     const checkLoading = setInterval(() => {
        //       if (!isLoading) {
        //         clearInterval(checkLoading);
        //         resolve();
        //       }
        //     }, 1000);
        //   }
        // });

        if (!userData) {
          navigate("/", { replace: true });
          return;
        }

        const isSubAdmin = (userData as any)?.role === 3;
        if (isSubAdmin && !permission) {
          navigate("/access-denied", { replace: true });
          return;
        }

        if (isSubAdmin && permission && !permission[page_permission]) {
          navigate("/access-denied", { replace: true });
        }
      } catch (error) {
        console.error("Error checking permissions:", error);
      } finally {
        setIsCheckingPermission(false);
      }
    };

    checkPermissionAndRedirect();
  }, [
    userData,
    permission,
    page_permission,
    navigate,
    // isLoading,
    isCheckingPermission,
  ]);

  // Return appropriate permissions based on role
  if (!userData) {
    return {
      hasPermission: false,
      permission: null,
      isAdd: false,
      isView: false,
      isEdit: false,
      isDelete: false,
      // isLoading: isLoading || isCheckingPermission,
    };
  }
  const isSubAdmin = (userData as any)?.role === 3;
  if (!isSubAdmin) {
    return {
      hasPermission: true,
      permission: null,
      isAdd: true,
      isView: true,
      isEdit: true,
      isDelete: true,
      // isLoading: isLoading || isCheckingPermission,
    };
  }

  if (isSubAdmin) {
    return {
      hasPermission: !!permission?.[page_permission],
      permission,
      isAdd: !!permission?.isAdd,
      isView: !!permission?.isView,
      isEdit: !!permission?.isEdit,
      isDelete: !!permission?.isDelete,
      // isLoading: isLoading || isCheckingPermission,
    };
  }

  // Default return for other cases (loading, no user, etc.)
  return {
    hasPermission: false,
    permission: null,
    isAdd: false,
    isView: false,
    isEdit: false,
    isDelete: false,
    // isLoading: isLoading || isCheckingPermission,
  };
};

export default usePermission;
